"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { MapPin, ArrowRight, Zap, ShieldCheck, Headset } from "lucide-react";

type Destination = { code: string; name: string; price?: string };

/**
 * Coastal Freedom hero per generated_homepage_or_overview.png — sunlit beach
 * lifestyle hero with a bold navy headline (one blue word) + an EMBEDDED PLAN
 * WIDGET (Country/Regional toggle + destination select + data/validity/price +
 * Buy Now). The widget is the brand's signature above-the-fold conversion
 * device; it resolves to the chosen destination page. Claim-safe trust chips.
 */
export default function Hero({ destinations }: { destinations: Destination[] }) {
  const { t } = useTranslations();
  const router = useRouter();
  const [scope, setScope] = useState<"country" | "regional">("country");
  const [code, setCode] = useState(destinations[0]?.code ?? "");

  const go = (e: React.FormEvent) => {
    e.preventDefault();
    if (scope === "country" && code) router.push(`/destinations/${code.toLowerCase()}/`);
    else router.push("/destinations/");
  };

  return (
    <section className="cf-hero-wash relative overflow-hidden">
      <div className="container mx-auto px-4 pt-10 pb-12 lg:pt-16 lg:pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {t("cf.hero.title1", "Stay Connected")}<br className="hidden sm:block" /> {t("cf.hero.title2", "Everywhere")} <span className="cf-accent">{t("cf.hero.title3", "You Go")}</span>
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl">
            {t("cf.hero.sub", "Internet that moves with you — enough vacation data to share, stream, navigate and hotspot for your whole trip. Ready in minutes, no roaming bill shock.")}
          </p>

          {/* embedded plan widget */}
          <form onSubmit={go} className="cf-card mt-6 p-4 max-w-md">
            <div className="inline-flex rounded-full bg-muted p-1 mb-3">
              {(["country", "regional"] as const).map((s) => (
                <button key={s} type="button" onClick={() => setScope(s)} className={`rounded-full px-4 h-8 text-sm font-semibold transition-colors ${scope === s ? "bg-primary text-primary-foreground" : "text-foreground/70"}`}>
                  {s === "country" ? t("cf.hero.country", "Country") : t("cf.hero.regional", "Regional")}
                </button>
              ))}
            </div>
            <div className="relative mb-3">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary" />
              <select value={code} onChange={(e) => setCode(e.target.value)} aria-label={t("cf.hero.destination", "Destination")} className="w-full h-11 rounded-lg border border-input bg-card pl-9 pr-3 text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                {destinations.map((d) => <option key={d.code} value={d.code}>{d.name}{d.price ? ` — ${t("destinations.fromPrice", "from {price}").replace("{price}", d.price)}` : ""}</option>)}
              </select>
            </div>
            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground font-bold h-11 transition-all active:scale-[0.98] hover:bg-primary/90 hover:-translate-y-0.5">
              {t("cf.hero.buy", "Find my vacation plan")} <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-primary" />{t("cf.hero.c1", "Instant delivery")}</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-primary" />{t("cf.hero.c2", "Secure checkout")}</span>
              <span className="inline-flex items-center gap-1"><Headset className="w-3.5 h-3.5 text-primary" />{t("cf.hero.c3", "24/7 support")}</span>
            </div>
          </form>
        </div>

        {/* lifestyle photo */}
        <div className="relative pl-4 sm:pl-10 lg:pl-16">
          {/* Premium lifestyle framed card */}
          <div className="relative aspect-[4/5] lg:aspect-[3/4] rounded-[2rem] overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] ring-1 ring-black/5 bg-slate-100">
            <ExportedImage
              src="/images/brands/coastal-freedom-travel-esim/coastal_hero_bg.webp"
              alt={t("cf.hero.bgAlt", "Aerial view of a tropical beach")}
              fill
              priority
              className="object-cover"
            />
          </div>
          
          {/* Floating glassmorphic badge */}
              <div className="absolute top-1/3 -left-2 sm:left-2 lg:left-6 z-20 animate-float bg-white/95 border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl p-4 flex items-center gap-3 w-[180px]">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0 text-blue-600">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 leading-tight">{t("cf.hero.badgeTitle", "Premium Data")}</p>
              <p className="text-[10px] font-medium text-slate-500 mt-0.5">{t("cf.hero.badgeSub", "High-speed 5G ready")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
